import { useAuthStore } from '@/stores/auth-store';

const API_URL = import.meta.env.VITE_API_URL || '';

interface RequestOptions extends RequestInit {
  auth?: boolean;
}

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Single-flight refresh: concurrent 401s all await the same refresh call.
let refreshPromise: Promise<boolean> | null = null;

function refreshTokens(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

async function doRefresh(): Promise<boolean> {
  const { refreshToken, setTokens, clearAuth } = useAuthStore.getState();

  if (!refreshToken) {
    clearAuth();
    return false;
  }

  try {
    const response = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      clearAuth();
      return false;
    }

    const data = await response.json();
    setTokens(data.accessToken, data.refreshToken);
    return true;
  } catch {
    clearAuth();
    return false;
  }
}

// Core request executor with 401 -> refresh -> retry-once handling.
// Works for both JSON requests and multipart uploads (FormData bodies).
async function send<T>(
  endpoint: string,
  init: RequestInit,
  auth: boolean,
  isRetry = false
): Promise<T> {
  const headers = new Headers(init.headers);

  if (auth) {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...init,
    headers,
  });

  if (response.ok) {
    return response.json();
  }

  // Handle token refresh on 401 (at most one retry per request)
  if (response.status === 401 && auth && !isRetry) {
    const refreshed = await refreshTokens();
    if (refreshed) {
      return send<T>(endpoint, init, auth, true);
    }
  }

  let errorData;
  try {
    errorData = await response.json();
  } catch {
    errorData = { message: response.statusText };
  }

  throw new ApiError(
    response.status,
    errorData.message || errorData.error || 'An error occurred',
    errorData
  );
}

function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { auth = true, headers: customHeaders, ...rest } = options;

  return send<T>(
    endpoint,
    {
      ...rest,
      headers: {
        'Content-Type': 'application/json',
        ...customHeaders,
      },
    },
    auth
  );
}

export const api = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, data?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  patch: <T>(endpoint: string, data?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }),

  delete: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),

  upload: <T>(
    endpoint: string,
    file: File,
    data?: Record<string, string>,
    options?: RequestOptions
  ): Promise<T> => {
    const { auth = true } = options || {};

    const formData = new FormData();
    formData.append('file', file);

    if (data) {
      Object.entries(data).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    // No Content-Type header: the browser sets the multipart boundary itself.
    return send<T>(
      endpoint,
      {
        method: 'POST',
        body: formData,
      },
      auth
    );
  },
};

export { ApiError };
